import { forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Button, Divider, FormLabel, Link, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PhoneInput } from '@sage/ui-components';

import { AuthErrorHeader } from './AuthHeader';
import parsePhoneNumber, { type E164Number } from 'libphonenumber-js/max';
import { AnalyticsContext } from 'src/AnalyticsContext';
import sageLogo from 'src/assets/images/sage-logo-on-light.svg';
import { useSageAuth } from 'src/auth/useSageAuth';
import AppVersion from 'src/components/fragments/AppVersion';
import { SageSupportPhoneNumber } from 'src/constants/ExternalUrls';
import {
    SageLoginCodePhoneNumberDev,
    SageLoginCodePhoneNumberProd,
} from 'src/constants/PhoneNumbers';
import { PreferredCountryCodes, SupportedCountryCodes } from 'src/constants/SupportedCountryCodes';
import useEnvironment from 'src/hooks/useEnvironment';
import { Formats, formatPhoneNumber } from 'src/utils/formUtils';
import {
    getPhoneNumberValidationRules,
    limitPhoneNumberLength,
} from 'src/utils/phoneNumberInputUtils';
import { snackActions } from 'src/utils/snackBarTools';

interface Auth0Error extends Error {
    code?: string;
    description?: string;
}

interface PhoneNumberLoginProps {
    onNext: (phoneNumber: E164Number) => void;
    onSSOLogin: () => void;
}

type FormValues = {
    phoneNumber: string;
};

const PageName = 'Phone Number Login page';

const PhoneNumberLogin = forwardRef<HTMLDivElement, PhoneNumberLoginProps>(
    function PhoneNumberLogin(props, ref) {
        const { onNext, onSSOLogin } = props;
        const { sendOtp, login } = useSageAuth();
        const { trackPageView, trackEvent } = useContext(AnalyticsContext);
        const { isProd } = useEnvironment();
        const { zIndex, spacing } = useTheme();

        const [isFetchingCode, setIsFetchingCode] = useState(false);
        const [phoneNumberValidationError, setPhoneNumberValidationError] = useState<Auth0Error>();
        const [isOfflineError, setIsOfflineError] = useState(false);
        const [isLoggingIn, setIsLoggingIn] = useState(false);

        const prodPhoneNumber = formatPhoneNumber(SageLoginCodePhoneNumberProd, Formats.National);
        const devPhoneNumber = formatPhoneNumber(SageLoginCodePhoneNumberDev, Formats.National);

        const { handleSubmit, control, formState } = useForm<FormValues>({
            mode: 'onChange',
            defaultValues: { phoneNumber: '' },
        });

        const { errorTitle, errorCaption } = useMemo(() => {
            if (isOfflineError) {
                return {
                    errorTitle: 'Your device is currently offline',
                    errorCaption: 'Please connect to the internet and try again',
                };
            }

            if (
                phoneNumberValidationError?.code === 'bad.connection' &&
                phoneNumberValidationError?.description?.toLowerCase() ===
                    'public signup is disabled'
            ) {
                return {
                    errorTitle: 'Not a correct phone number',
                    errorCaption:
                        'Check the number below for mistakes. If the number is correct, ask your manager to check your profile',
                };
            }

            if (phoneNumberValidationError?.description?.includes('21610')) {
                return {
                    errorTitle: 'Your phone is unsubscribed from login codes',
                    errorCaption: `Text "start" to ${
                        isProd ? prodPhoneNumber : devPhoneNumber
                    } from the phone number you entered and try again.`,
                };
            }
            return {
                errorTitle: 'Something went wrong',
                errorCaption: (
                    <Typography variant="body1">
                        <span>
                            Try logging in again. If it still doesn&apos;t work, contact&nbsp;
                        </span>
                        <Link
                            href={`tel:${SageSupportPhoneNumber}`}
                            sx={{ color: 'white.main', textDecoration: 'underline' }}
                        >
                            Sage Support
                        </Link>
                        <span>&nbsp;for help.</span>
                    </Typography>
                ),
            };
        }, [
            devPhoneNumber,
            isOfflineError,
            isProd,
            phoneNumberValidationError?.code,
            phoneNumberValidationError?.description,
            prodPhoneNumber,
        ]);

        const onSubmit: SubmitHandler<FormValues> = (data: FormValues) => {
            setIsFetchingCode(true);
            setIsOfflineError(false);
            setPhoneNumberValidationError(undefined);

            trackEvent('Phone Number Submitted', { pageName: PageName });

            if (!navigator.onLine) {
                setIsOfflineError(true);
                setIsFetchingCode(false);
                return;
            }

            let cleanPhoneNumber: E164Number | undefined;
            try {
                cleanPhoneNumber = parsePhoneNumber(data.phoneNumber)?.number;
            } catch (parseError: any) {
                datadogLogs.logger.error(
                    'Error parsing phone number',
                    { message: 'Error parsing phone number', input: data.phoneNumber },
                    parseError
                );
                setPhoneNumberValidationError(parseError);
                return;
            }

            sendOtp(cleanPhoneNumber!.toString())
                .then(() => {
                    onNext(cleanPhoneNumber!);
                })
                .catch((err: any) => {
                    setPhoneNumberValidationError(err);
                    window.scrollTo({ top: 0 });

                    /**
                     * "bad.connection" related errors are not actionable because they indicate
                     * scenarios where users are trying to use passwordless login using a phone
                     * number that is not associated with a known account in Sage's Auth0 tenant.
                     *
                     * Therefore, we log these as warnings instead of errors.
                     */
                    const { code, description } = err;
                    const messageContext = {
                        message: 'Error requesting passwordless code',
                        input: data.phoneNumber,
                        code,
                        description,
                    };

                    if (code !== 'bad.connection' && description !== 'Public signup is disabled') {
                        datadogLogs.logger.error(
                            'Error requesting passwordless code',
                            messageContext,
                            err
                        );
                    } else {
                        datadogLogs.logger.warn(
                            'Error requesting passwordless code',
                            messageContext,
                            err
                        );
                    }
                })
                .finally(() => {
                    setIsFetchingCode(false);
                });
        };

        const handleLoginRedirect = async () => {
            trackEvent('Log In Using Email Clicked', { pageName: PageName });
            setIsOfflineError(false);
            if (!navigator.onLine) {
                setIsOfflineError(true);
                return;
            }
            setIsLoggingIn(true);
            try {
                await login({ forceLogin: true });
            } catch (loginError) {
                datadogRum.addError(loginError, { context: 'emailLogin' });
                snackActions.error('Unable to start email login. Please try again.');
                setIsLoggingIn(false);
            }
        };

        const handlePhoneNumberChange = (value: string, onChange: (value: any) => void) => {
            if (value.startsWith('+1 1 ')) {
                // Remove the extra '1' that is added by the library
                onChange(limitPhoneNumberLength(value.replace('+1 1 ', '+1 ')));
            } else if (!value.startsWith('+1')) {
                // Add the default country code if it is missing
                onChange(limitPhoneNumberLength(`+1 ${value}`));
            } else {
                onChange(limitPhoneNumberLength(value));
            }
        };

        useEffect(() => {
            trackPageView(PageName);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
            <Box ref={ref} component="main" sx={{ minHeight: '100dvh' }}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: zIndex.modal,
                    }}
                >
                    {(phoneNumberValidationError || isOfflineError) && (
                        <AuthErrorHeader title={errorTitle} caption={errorCaption} />
                    )}
                </Box>
                <Box sx={{ p: 2.5, flexGrow: 1 }}>
                    <Stack sx={{ gap: 2 }}>
                        <Box
                            alt="Sage"
                            component="img"
                            src={sageLogo}
                            sx={{
                                display: 'block',
                                width: spacing(14),
                                objectFit: 'contain',
                            }}
                        />
                        <Typography component="h1" variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Welcome to Sage!
                        </Typography>
                    </Stack>

                    <Typography variant="body2" component="p" sx={{ mb: 4 }}>
                        Enter your phone number below to continue.
                    </Typography>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack sx={{ gap: 2 }}>
                            <Stack sx={{ gap: 0.5 }}>
                                <FormLabel required htmlFor="phone-number-input">
                                    Phone Number
                                </FormLabel>
                                <Controller
                                    name="phoneNumber"
                                    control={control}
                                    rules={getPhoneNumberValidationRules({ isRequired: true })}
                                    render={({
                                        field: { ref: fieldRef, onChange, value, ...fieldProps },
                                        fieldState: { invalid, error },
                                    }) => (
                                        <PhoneInput
                                            {...fieldProps}
                                            forceCallingCode
                                            focusOnSelectCountry
                                            value={value ?? ''}
                                            inputRef={fieldRef}
                                            onlyCountries={SupportedCountryCodes}
                                            preferredCountries={PreferredCountryCodes}
                                            defaultCountry={PreferredCountryCodes[0]}
                                            onChange={(value) =>
                                                handlePhoneNumberChange(value, onChange)
                                            }
                                            // TextFieldProps
                                            error={invalid}
                                            fullWidth
                                            helperText={error?.message}
                                            id="phone-number-input"
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </Stack>

                            <Button
                                disableElevation
                                disabled={isFetchingCode || !formState.isValid}
                                fullWidth
                                loading={isFetchingCode}
                                type="submit"
                                variant="contained"
                            >
                                Continue
                            </Button>
                        </Stack>
                    </form>

                    <Stack sx={{ mt: 2.5, gap: 2.5 }}>
                        <Typography variant="body2" sx={{ color: 'grey.900' }}>
                            By continuing, you agree to receive a 6-digit verification code via text
                            message.
                        </Typography>

                        <Divider>
                            <Typography
                                variant="body1"
                                component="span"
                                sx={{ color: 'grey.500', textTransform: 'uppercase' }}
                            >
                                or
                            </Typography>
                        </Divider>

                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleLoginRedirect}
                            loading={isLoggingIn}
                        >
                            Log In Using Email
                        </Button>
                        <Button variant="outlined" fullWidth onClick={onSSOLogin}>
                            Log In Using Single Sign-on
                        </Button>
                    </Stack>
                </Box>
                <AppVersion />
            </Box>
        );
    }
);

export default PhoneNumberLogin;
